/**
 * Dialog para criação de novo Event Type
 * 
 * Formulário com campos essenciais para criar um tipo de evento.
 * Usa a RPC create_scheduling_event_type que já cria disponibilidade padrão.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSchedulingEventTypes, type CreateEventTypeInput } from '@/hooks/useSchedulingEventTypes';
import { useAuth } from '@/hooks/useAuth';

interface CreateEventTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Gera slug a partir do título (lowercase, sem acentos, hifenizado) */
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}

/** Opções de duração pré-definidas */
const DURATION_OPTIONS = [
  { value: '15', label: '15 minutos' },
  { value: '30', label: '30 minutos' },
  { value: '45', label: '45 minutos' },
  { value: '60', label: '1 hora' },
  { value: '90', label: '1h 30min' },
  { value: '120', label: '2 horas' },
];

/** Opções de cores */
const COLOR_OPTIONS = [
  '#3B82F6', // azul
  '#10B981', // verde
  '#F59E0B', // amarelo
  '#EF4444', // vermelho
  '#8B5CF6', // roxo
  '#EC4899', // rosa
  '#06B6D4', // ciano
  '#F97316', // laranja
];

export function CreateEventTypeDialog({ open, onOpenChange }: CreateEventTypeDialogProps) {
  const { createEventType, isCreating } = useSchedulingEventTypes();
  const { currentTenant } = useAuth();

  // Estado do formulário
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('30');
  const [color, setColor] = useState('#3B82F6');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  /** Atualiza título e slug automaticamente */
  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(generateSlug(value));
    }
  };

  /** Reset do formulário ao fechar */
  const handleClose = () => {
    setTitle('');
    setSlug('');
    setDescription('');
    setDuration('30');
    setColor('#3B82F6');
    setSlugManuallyEdited(false);
    onOpenChange(false);
  };

  /** Submissão do formulário */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !slug.trim()) return;

    const input: CreateEventTypeInput = {
      title: title.trim(),
      slug: slug.trim(),
      description: description.trim() || undefined,
      duration_minutes: parseInt(duration),
      color,
    };

    await createEventType(input);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Novo Tipo de Evento</DialogTitle>
            <DialogDescription>
              Configure as informações básicas do seu tipo de evento. A disponibilidade padrão (Seg-Sex, 09h-17h) será criada automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                placeholder="Ex: Reunião de Consultoria"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                maxLength={100}
                required
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL) *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">/schedule/{currentTenant?.slug || 'empresa'}/</span>
                <Input
                  id="slug"
                  placeholder="reuniao-consultoria"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSlugManuallyEdited(true);
                  }}
                  maxLength={60}
                  className="font-mono text-sm"
                  required
                />
              </div>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                placeholder="Descreva brevemente o tipo de evento..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                rows={3}
              />
            </div>

            {/* Duração */}
            <div className="space-y-2">
              <Label>Duração</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cor */}
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 transition-all ${
                      color === c
                        ? 'border-foreground scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating || !title.trim() || !slug.trim()}>
              {isCreating ? 'Criando...' : 'Criar Evento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
